/* eslint-disable max-len */
import React, {useState, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {Editor, Element} from 'slate';

import {useConfigStore} from '../../store/reportStore/configStore';
import {reportDialog} from '../../store/reportStore/reportDialogStore';

import '../../styles/_reportComponents.scss';
import '../../styles/_editor.scss';

import {PRESET_COLORS, sanitizeRange, setLineSpacing} from './constants';
import ToolbarIcons from './ToolbarIcons';

// --- Types for Color Calculations ---
interface RGB {
  r: number;
  g: number;
  b: number;
}
interface HSV {
  h: number;
  s: number;
  v: number;
}

// --- Color Conversion Helpers ---
const hexToRgb = (hex: string): RGB | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const rgbToHsv = (r: number, g: number, b: number): HSV => {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s: number;
  const v = max;
  const d = max - min;
  // eslint-disable-next-line prefer-const
  s = max === 0 ? 0 : d / max;
  if (max === min) {
    h = 0;
  } else {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {h: h * 360, s: s * 100, v: v * 100};
};

const hsvToRgb = (h: number, s: number, v: number): RGB => {
  let r = 0;
  let g = 0;
  let b = 0;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

// start of linespacing

export const LineSpacingButton = ({
  editor,
  selectedRanges = []
}: any): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as HTMLElement)
      )
        setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  let currentSpacing = 1.5;
  if (editor.selection) {
    const sanitized = sanitizeRange(editor, editor.selection);
    if (sanitized) {
      const [match] = Array.from(
        Editor.nodes(editor, {
          at: sanitized,
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            n.lineHeight !== undefined
        })
      );
      if (match) currentSpacing = (match[0] as any).lineHeight;
    }
  }

  return (
    <div
      ref={buttonRef}
      style={{position: 'relative', display: 'inline-block'}}
    >
      <button
        className="toolbar-btn"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
      >
        <ToolbarIcons tool="lineHeight" />
        <span style={{fontSize: '13px', fontWeight: 'bold'}}>
          {currentSpacing}
        </span>
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            width: '60px'
          }}
        >
          {[1, 1.2, 1.5, 2].map((opt) => (
            <button
              key={opt}
              style={{
                padding: '8px',
                border: 'none',
                background: opt === currentSpacing ? '#e8f0fe' : 'transparent'
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                setLineSpacing(editor, opt, selectedRanges);
                setIsOpen(false);
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// end of linespacing

// --- START OF ColorPickerModal.tsx ---
const COLOR_PICKER_MODAL_WIDTH = 320;
const COLOR_PICKER_MODAL_HEIGHT_EST = 420;

interface ColorPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChange: (color: string) => void;
  initialColor?: string;
  title?: string;
  /** When set, panel is fixed under this rect (e.g. toolbar button). Otherwise centered in viewport. */
  anchorRect?: DOMRect | null;
}

export const ColorPickerModal: React.FC<ColorPickerModalProps> = ({
  isOpen,
  onClose,
  onChange,
  initialColor = '#ffffff',
  title = 'Select Color',
  anchorRect = null
}) => {
  const [hex, setHex] = useState(initialColor);
  const [hsv, setHsv] = useState<HSV>({h: 0, s: 0, v: 100});
  const satRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialColor && isOpen) {
      const rgb = hexToRgb(initialColor);
      if (rgb) {
        setHsv(rgbToHsv(rgb.r, rgb.g, rgb.b));
        setHex(initialColor);
      }
    }
  }, [initialColor, isOpen]);

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setHex(val);
    const rgb = hexToRgb(val);
    if (rgb) {
      setHsv(rgbToHsv(rgb.r, rgb.g, rgb.b));
      onChange(val);
    }
  };

  const handlePresetClick = (color: string) => {
    setHex(color);
    const rgb = hexToRgb(color);
    if (rgb) {
      setHsv(rgbToHsv(rgb.r, rgb.g, rgb.b));
      onChange(color);
    }
  };

  const handleSatMove = (e: MouseEvent | React.MouseEvent) => {
    if (!satRef.current) return;
    const rect = satRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    x = Math.max(0, Math.min(x, rect.width));
    y = Math.max(0, Math.min(y, rect.height));

    const s = (x / rect.width) * 100;
    const v = 100 - (y / rect.height) * 100;

    const newHsv = {...hsv, s, v};
    setHsv(newHsv);
    const rgb = hsvToRgb(newHsv.h / 360, s / 100, v / 100);
    const newHex = rgbToHex(rgb.r, rgb.g, rgb.b);
    setHex(newHex);
    onChange(newHex);
  };

  const handleSatMouseUp = () => {
    document.removeEventListener('mousemove', handleSatMove as any);
    document.removeEventListener('mouseup', handleSatMouseUp);
  };

  const handleSatMouseDown = (e: React.MouseEvent) => {
    handleSatMove(e);
    document.addEventListener('mousemove', handleSatMove as any);
    document.addEventListener('mouseup', handleSatMouseUp);
  };

  const handleHueMove = (e: MouseEvent | React.MouseEvent) => {
    if (!hueRef.current) return;
    const rect = hueRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));
    const h = (x / rect.width) * 360;

    const newHsv = {...hsv, h};
    setHsv(newHsv);
    const rgb = hsvToRgb(h / 360, newHsv.s / 100, newHsv.v / 100);
    const newHex = rgbToHex(rgb.r, rgb.g, rgb.b);
    setHex(newHex);
    onChange(newHex);
  };

  const handleHueMouseUp = () => {
    document.removeEventListener('mousemove', handleHueMove as any);
    document.removeEventListener('mouseup', handleHueMouseUp);
  };

  const handleHueMouseDown = (e: React.MouseEvent) => {
    handleHueMove(e);
    document.addEventListener('mousemove', handleHueMove as any);
    document.addEventListener('mouseup', handleHueMouseUp);
  };

  if (!isOpen) return null;

  const anchored = anchorRect != null;
  let modalStyle: React.CSSProperties | undefined;
  if (anchored && anchorRect) {
    const left = Math.min(
      Math.max(8, anchorRect.left),
      window.innerWidth - COLOR_PICKER_MODAL_WIDTH - 8
    );
    let top = anchorRect.bottom + 8;
    if (top + COLOR_PICKER_MODAL_HEIGHT_EST > window.innerHeight - 8) {
      top = Math.max(8, anchorRect.top - COLOR_PICKER_MODAL_HEIGHT_EST - 8);
    }
    modalStyle = {position: 'fixed' as const, top, left, margin: 0};
  }

  const tree = (
    <div
      className={`color-picker-overlay reportComponents${
        anchored ? ' color-picker-overlay--anchored' : ''
      }`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="color-picker-modal"
        style={modalStyle}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="color-picker-header">
          <h3>{title}</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="color-picker-body">
          <div
            className="saturation-area"
            ref={satRef}
            onMouseDown={handleSatMouseDown}
            style={{backgroundColor: `hsl(${hsv.h}, 100%, 50%)`}}
          >
            <div className="saturation-white" />
            <div className="saturation-black" />
            <div
              className="saturation-cursor"
              style={{left: `${hsv.s}%`, top: `${100 - hsv.v}%`}}
            />
          </div>
          <div
            className="hue-slider"
            ref={hueRef}
            onMouseDown={handleHueMouseDown}
          >
            <div
              className="hue-cursor"
              style={{left: `${(hsv.h / 360) * 100}%`}}
            />
          </div>
          <div className="preset-colors">
            {PRESET_COLORS.map((c, i) => (
              <div
                key={i}
                className="color-swatch"
                style={{backgroundColor: c}}
                onClick={() => {
                  handlePresetClick(c);
                }}
                title={c}
              />
            ))}
          </div>
          <div className="hex-input-wrapper">
            <label>Hex</label>
            <input
              type="text"
              value={hex}
              onChange={handleHexChange}
              maxLength={7}
            />
            <div
              className="current-color-preview"
              style={{backgroundColor: hex}}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(tree, document.body);
};

// --- START OF PageMarginsModal.tsx ---
interface PageMarginsModalProps {
  isOpen: boolean;
  anchorRect?: DOMRect | null;
  onClose: () => void;
  onApply: (margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  }) => void;
  initialMargins: any;
  pageOrientationLabel: string;
}

export const PageMarginsModal: React.FC<PageMarginsModalProps> = ({
  isOpen,
  anchorRect = null,
  onClose,
  onApply,
  initialMargins,
  pageOrientationLabel
}) => {
  const [draft, setDraft] = useState({
    top: '0.5',
    right: '0.5',
    bottom: '0.5',
    left: '0.5'
  });

  useEffect(() => {
    if (!isOpen) return;
    const toDisplay = (val: number) => String(Math.round(val * 100) / 100);
    setDraft({
      top: toDisplay(initialMargins?.top ?? 0.5),
      right: toDisplay(initialMargins?.right ?? 0.5),
      bottom: toDisplay(initialMargins?.bottom ?? 0.5),
      left: toDisplay(initialMargins?.left ?? 0.5)
    });
  }, [isOpen, initialMargins]);

  if (!isOpen) return null;

  const MODAL_WIDTH = 420;
  const MODAL_HEIGHT_EST = 320;
  const VIEWPORT_PADDING = 12;
  const ANCHOR_GAP = 8;
  const anchored = anchorRect != null;
  const computedLeft = anchorRect
    ? Math.min(
        Math.max(VIEWPORT_PADDING, anchorRect.left),
        window.innerWidth - MODAL_WIDTH - VIEWPORT_PADDING
      )
    : undefined;
  let computedTop = anchorRect ? anchorRect.bottom + ANCHOR_GAP : 0;
  if (anchorRect && computedTop + MODAL_HEIGHT_EST > window.innerHeight - 8) {
    computedTop = Math.max(
      VIEWPORT_PADDING,
      anchorRect.top - MODAL_HEIGHT_EST - ANCHOR_GAP
    );
  }

  const handleApply = () => {
    const clamp = (v: string) => Math.max(0, Math.min(3, parseFloat(v) || 0.5));
    onApply({
      top: clamp(draft.top),
      right: clamp(draft.right),
      bottom: clamp(draft.bottom),
      left: clamp(draft.left)
    });
    onClose();
  };

  return (
    <div
      className="page-margins-overlay reportComponents"
      onMouseDown={onClose}
      style={
        anchored
          ? {
              backgroundColor: 'transparent',
              alignItems: 'flex-start',
              zIndex: 2000
            }
          : undefined
      }
    >
      <div
        className="page-margins-modal"
        style={
          anchored
            ? {
                width: MODAL_WIDTH,
                position: 'fixed',
                left: computedLeft,
                top: computedTop,
                margin: 0,
                zIndex: 2001
              }
            : undefined
        }
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="page-margins-header">
          <h3>Custom Margins</h3>
          <button className="page-margins-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="page-margins-body">
          <div className="page-margins-subtitle">
            Page Orientation: {pageOrientationLabel}
          </div>
          <div className="page-margins-grid">
            {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
              <label key={side}>
                {side.charAt(0).toUpperCase() + side.slice(1)} (in)
                <input
                  type="number"
                  step="0.05"
                  value={draft[side]}
                  onChange={(e) => {
                    setDraft((p) => ({...p, [side]: e.target.value}));
                  }}
                />
              </label>
            ))}
          </div>
        </div>
        <div className="page-margins-footer">
          <button className="page-margins-btn cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="page-margins-btn apply" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// --- START OF WatermarkModal.tsx ---
interface WatermarkModalProps {
  isOpen: boolean;
  anchorRect?: DOMRect | null;
  onClose: () => void;
  onApply: (watermark: any) => void;
  initialWatermark: null | any;
}

export const WatermarkModal: React.FC<WatermarkModalProps> = ({
  isOpen,
  anchorRect = null,
  onClose,
  onApply,
  initialWatermark
}) => {
  const config = useConfigStore((s) => s.watermark);
  const [text, setText] = useState('');
  const [font, setFont] = useState(config.fontFamily);
  const [size, setSize] = useState<string>(String(config.fontSize));
  const [color, setColor] = useState(config.color);
  const [layout, setLayout] = useState(config.layout);
  const [transparency, setTransparency] = useState(true);

  useEffect(() => {
    if (isOpen && initialWatermark) {
      setText(initialWatermark.text || '');
      setFont(initialWatermark.fontFamily || config.fontFamily);
      setSize(String(initialWatermark.fontSize || config.fontSize));
      setColor(initialWatermark.color || config.color);
      setLayout(initialWatermark.layout || config.layout);
      setTransparency((initialWatermark.opacity || 1) < 1);
    }
  }, [isOpen, initialWatermark, config]);

  if (!isOpen) return null;

  const MODAL_WIDTH = 420;
  const MODAL_HEIGHT_EST = 360;
  const VIEWPORT_PADDING = 12;
  const ANCHOR_GAP = 8;
  const anchored = anchorRect != null;
  const computedLeft = anchorRect
    ? Math.min(
        Math.max(VIEWPORT_PADDING, anchorRect.left),
        window.innerWidth - MODAL_WIDTH - VIEWPORT_PADDING
      )
    : undefined;
  let computedTop = anchorRect ? anchorRect.bottom + ANCHOR_GAP : 0;
  if (anchorRect && computedTop + MODAL_HEIGHT_EST > window.innerHeight - 8) {
    computedTop = Math.max(
      VIEWPORT_PADDING,
      anchorRect.top - MODAL_HEIGHT_EST - ANCHOR_GAP
    );
  }

  const handleSubmit = () => {
    onApply({
      text,
      fontFamily: font,
      fontSize: parseInt(size) || 48,
      color,
      opacity: transparency ? 0.5 : 1,
      layout
    });
    onClose();
  };

  return (
    <div
      className="watermark-modal-overlay reportComponents"
      onMouseDown={onClose}
      style={
        anchored
          ? {
              backgroundColor: 'transparent',
              alignItems: 'flex-start',
              zIndex: 2000
            }
          : undefined
      }
    >
      <div
        className="watermark-modal"
        style={
          anchored
            ? {
                width: MODAL_WIDTH,
                position: 'fixed',
                left: computedLeft,
                top: computedTop,
                margin: 0,
                zIndex: 2001
              }
            : undefined
        }
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="watermark-modal-header">
          <h3>Text watermark</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="watermark-modal-body">
          <div className="watermark-form-group">
            <label>Text:</label>
            <input
              type="text"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
              }}
              placeholder="ASAP, CONFIDENTIAL"
            />
          </div>
          <div className="watermark-form-group">
            <label>Font:</label>
            <select
              value={font}
              onChange={(e) => {
                setFont(e.target.value);
              }}
            >
              {[
                'Arial',
                'Times New Roman',
                'Courier New',
                'Verdana',
                'Calibri'
              ].map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div
            className="watermark-form-group"
            style={{flexDirection: 'row', gap: 10}}
          >
            <div style={{flex: 1}}>
              <label>Size:</label>
              <input
                type="number"
                value={size}
                onChange={(e) => {
                  setSize(e.target.value);
                }}
              />
            </div>
            <div style={{flex: 1}}>
              <label>Color:</label>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <input
                  type="color"
                  value={color}
                  onChange={(e) => {
                    setColor(e.target.value);
                  }}
                />
                <label style={{fontSize: '12px'}}>
                  <input
                    type="checkbox"
                    checked={transparency}
                    onChange={(e) => {
                      setTransparency(e.target.checked);
                    }}
                  />{' '}
                  Transparent
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className="watermark-modal-footer">
          <button className="watermark-btn cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="watermark-btn apply" onClick={handleSubmit}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// --- START OF PageNumberModal.tsx ---
interface PageNumberModalProps {
  isOpen: boolean;
  anchorRect?: DOMRect | null;
  onClose: () => void;
  onApply: (settings: {
    position: string;
    alignment: string;
    format: string;
  }) => void;
  onRemove?: () => void;
}

export const PageNumberModal: React.FC<PageNumberModalProps> = ({
  isOpen,
  anchorRect = null,
  onClose,
  onApply,
  onRemove
}) => {
  const [position, setPosition] = useState('header');
  const [alignment, setAlignment] = useState('right');
  const [format, setFormat] = useState('123');

  if (!isOpen) return null;

  const MODAL_WIDTH = 340;
  const MODAL_HEIGHT_EST = 380;
  const VIEWPORT_PADDING = 12;
  const ANCHOR_GAP = 8;
  const anchored = anchorRect != null;
  const computedLeft = anchorRect
    ? Math.min(
        Math.max(VIEWPORT_PADDING, anchorRect.left),
        window.innerWidth - MODAL_WIDTH - VIEWPORT_PADDING
      )
    : undefined;
  let computedTop = anchorRect ? anchorRect.bottom + ANCHOR_GAP : 0;
  if (anchorRect && computedTop + MODAL_HEIGHT_EST > window.innerHeight - 8) {
    computedTop = Math.max(
      VIEWPORT_PADDING,
      anchorRect.top - MODAL_HEIGHT_EST - ANCHOR_GAP
    );
  }

  const handleApply = () => {
    onApply({position, alignment, format});
    onClose();
  };

  return (
    <div
      className="watermark-modal-overlay reportComponents"
      onMouseDown={onClose}
      style={
        anchored
          ? {
              backgroundColor: 'transparent',
              alignItems: 'flex-start',
              zIndex: 2000
            }
          : undefined
      }
    >
      <div
        className="watermark-modal"
        style={
          anchored
            ? {
                width: MODAL_WIDTH,
                position: 'fixed',
                left: computedLeft,
                top: computedTop,
                margin: 0,
                zIndex: 2001
              }
            : {width: MODAL_WIDTH}
        }
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="watermark-modal-header">
          <h3>Page numbers</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="watermark-modal-body" style={{gap: 20}}>
          <div className="watermark-form-group">
            <label>Position</label>
            <div className="watermark-layout-options">
              {['header', 'footer'].map((pos) => (
                <label key={pos} className="radio-label">
                  <input
                    type="radio"
                    name="pos"
                    checked={position === pos}
                    onChange={() => {
                      setPosition(pos);
                    }}
                  />
                  {pos.charAt(0).toUpperCase() + pos.slice(1)}
                </label>
              ))}
            </div>
          </div>
          <div className="watermark-form-group">
            <label>Alignment</label>
            <select
              className="watermark-select"
              value={alignment}
              onChange={(e) => {
                setAlignment(e.target.value);
              }}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div className="watermark-form-group">
            <label>Format</label>
            <div className="watermark-layout-options watermark-layout-options--column">
              {['123', 'abc', 'roman'].map((f) => (
                <label key={f} className="radio-label">
                  <input
                    type="radio"
                    name="fmt"
                    checked={format === f}
                    onChange={() => {
                      setFormat(f);
                    }}
                  />
                  {f === '123'
                    ? '1, 2, 3...'
                    : f === 'abc'
                      ? 'a, b, c...'
                      : 'i, ii, iii...'}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="watermark-modal-footer watermark-modal-footer--split">
          <button
            className="watermark-btn remove"
            onClick={() => {
              onRemove?.();
              onClose();
            }}
          >
            Remove
          </button>
          <div style={{display: 'flex', gap: 10}}>
            <button className="watermark-btn cancel" onClick={onClose}>
              Cancel
            </button>
            <button className="watermark-btn apply" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- START OF SettingsModal.tsx ---
interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose
}) => {
  const config = useConfigStore();
  const updateConfig = useConfigStore((s) => s.updateConfig);
  const resetToDefaults = useConfigStore((s) => s.resetToDefaults);

  if (!isOpen) return null;

  return (
    <div className="watermark-modal-overlay reportComponents">
      <div className="watermark-modal" style={{width: 450}}>
        <div className="watermark-modal-header">
          <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
            <h3 style={{margin: 0, fontSize: '18px', fontWeight: 600}}>
              User Configuration
            </h3>
          </div>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div
          className="watermark-modal-body"
          style={{maxHeight: '70vh', overflowY: 'auto', padding: '24px'}}
        >
          <div className="watermark-form-group">
            <label>Page Orientation</label>
            <select
              className="watermark-select"
              value={config.pageOrientation}
              onChange={(e) => {
                updateConfig({
                  pageOrientation: e.target.value as 'portrait' | 'landscape'
                });
              }}
            >
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </div>
          <div className="watermark-form-group">
            <label>Default Line Spacing</label>
            <select
              className="watermark-select"
              value={config.lineSpacing}
              onChange={(e) => {
                updateConfig({lineSpacing: parseFloat(e.target.value)});
              }}
            >
              {[1, 1.15, 1.5, 2].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div
          className="watermark-modal-footer"
          style={{backgroundColor: '#f1f3f4', justifyContent: 'space-between'}}
        >
          <button
            className="watermark-btn"
            onClick={() => {
              reportDialog.confirm({
                title: 'Reset defaults',
                message: 'Reset all defaults?',
                variant: 'danger',
                confirmLabel: 'Reset',
                onConfirm: () => {
                  resetToDefaults();
                }
              });
            }}
            style={{color: '#d93025'}}
          >
            Reset
          </button>
          <button
            className="watermark-btn apply"
            onClick={onClose}
            style={{backgroundColor: '#1a73e8', color: '#fff'}}
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};
// --- START OF HeaderFooterModal.tsx ---
interface HeaderFooterModalProps {
  isOpen: boolean;
  anchorRect?: DOMRect | null;
  onClose: () => void;
  onSelect: (type: string) => void;
  title: string;
}

export const HeaderFooterModal: React.FC<HeaderFooterModalProps> = ({
  isOpen,
  anchorRect,
  onClose,
  onSelect,
  title
}) => {
  if (!isOpen) return null;

  const templates = [
    {id: 'default', name: 'Default', description: 'Simple centered text'},
    {
      id: 'three-columns',
      name: 'Three Columns',
      description: 'Left, Center, and Right slots'
    },
    {
      id: 'banded',
      name: 'Banded',
      description: 'Dark background with white text'
    },
    {id: 'austin', name: 'Austin', description: 'Bordered box'},
    {id: 'remove', name: 'Remove', description: 'Remove Header/Footer'}
  ];

  const MODAL_WIDTH = 500;
  const VIEWPORT_PADDING = 12;
  const ANCHOR_GAP = 8;
  const computedLeft = anchorRect
    ? Math.min(
        Math.max(VIEWPORT_PADDING, anchorRect.left),
        window.innerWidth - MODAL_WIDTH - VIEWPORT_PADDING
      )
    : undefined;
  const computedTop = anchorRect
    ? Math.min(
        anchorRect.bottom + ANCHOR_GAP,
        window.innerHeight - VIEWPORT_PADDING - 360
      )
    : undefined;

  return createPortal(
    <div
      className="watermark-modal-overlay reportComponents header-footer-modal-overlay"
      onMouseDown={onClose}
      style={
        anchorRect
          ? {
              backgroundColor: 'transparent',
              alignItems: 'flex-start',
              zIndex: 2000
            }
          : undefined
      }
    >
      <div
        className="watermark-modal"
        style={
          anchorRect
            ? {
                width: MODAL_WIDTH,
                position: 'fixed',
                left: computedLeft,
                top: computedTop,
                zIndex: 2001
              }
            : {width: MODAL_WIDTH}
        }
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="watermark-modal-header">
          <h3>{title}</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div
          className="watermark-modal-body"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            padding: '20px'
          }}
        >
          {templates.map((template) => (
            <div
              key={template.id}
              className="template-card"
              onClick={() => {
                onSelect(template.id);
                onClose();
              }}
              style={{
                border: '1px solid #dadce0',
                borderRadius: '8px',
                padding: '12px',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
            >
              <div style={{fontWeight: 600, marginBottom: '4px'}}>
                {template.name}
              </div>
              <div style={{fontSize: '12px', color: '#5f6368'}}>
                {template.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};
